import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSearch, FaMapMarkerAlt, FaBuilding } from 'react-icons/fa';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { searchProjects } from '../services/api';
import './SearchBar.css';

const SearchBar = ({ onSelectAddress, onSelectProject }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchMode, setSearchMode] = useState('address'); // 'address' ou 'project'

  // Fonction pour récupérer les suggestions d'adresse
  const fetchAddressSuggestions = useCallback(async (input) => {
    if (input && input.length > 2 && isSearching && searchMode === 'address') {
      try {
        const response = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: {
            q: input,
            format: 'json',
            addressdetails: 1,
            limit: 5
          }
        });
        setSuggestions(response.data);
        setError(null);
      } catch (error) {
        console.error(error);
        setError('Une erreur est survenue lors de la récupération des suggestions.');
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
      setError(null);
    }
  }, [isSearching, searchMode]);

  // Fonction pour récupérer les suggestions de projets
  const fetchProjectSuggestions = useCallback(async (input) => {
    if (input && input.length > 2 && isSearching && searchMode === 'project') {
      try {
        const data = await searchProjects(input);
        setSuggestions(data);
        setError(null);
      } catch (error) {
        console.error(error);
        setError('Une erreur est survenue lors de la recherche des projets.');
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
      setError(null);
    }
  }, [isSearching, searchMode]);

  // Débounçage des fonctions de recherche
  const debouncedFetchAddressSuggestions = useMemo(
    () => debounce(fetchAddressSuggestions, 300),
    [fetchAddressSuggestions]
  );

  const debouncedFetchProjectSuggestions = useMemo(
    () => debounce(fetchProjectSuggestions, 300),
    [fetchProjectSuggestions]
  );

  useEffect(() => {
    if (searchMode === 'address') {
      debouncedFetchAddressSuggestions(searchTerm);
    } else {
      debouncedFetchProjectSuggestions(searchTerm);
    }
    return () => {
      debouncedFetchAddressSuggestions.cancel();
      debouncedFetchProjectSuggestions.cancel();
    };
  }, [searchTerm, searchMode, debouncedFetchAddressSuggestions, debouncedFetchProjectSuggestions]);

  const handleClear = () => {
    setSearchTerm('');
    setSuggestions([]);
    setError(null);
    setIsSearching(false);
    setActiveIndex(0);
  };

  const handleSelect = (suggestion) => {
    if (searchMode === 'address') {
      setSearchTerm(formatAddress(suggestion.address));
      // S'assurer que les coordonnées sont bien des nombres
      const lat = parseFloat(suggestion.lat);
      const lon = parseFloat(suggestion.lon);
      if (!isNaN(lat) && !isNaN(lon) && onSelectAddress) {
        onSelectAddress({
          lat,
          lon,
          address: suggestion.address,
        });
      }
    } else {
      setSearchTerm(suggestion.titre);
      if (onSelectProject) {
        // S'assurer que les coordonnées sont bien des nombres
        const project = {
          ...suggestion,
          latitude: parseFloat(suggestion.latitude),
          longitude: parseFloat(suggestion.longitude)
        };
        onSelectProject(project);
      }
    }
    setSuggestions([]);
    setError(null);
    setIsSearching(false);
    setActiveIndex(0);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsSearching(true);
    setActiveIndex(0);
  };

  const formatAddress = (address) => {
    const components = [];

    // Gestion des adresses détaillées
    if (address.house_number || address.road) {
      const streetAddress = [address.house_number, address.road]
        .filter(Boolean)
        .join(' ');
      components.push(streetAddress);
    }

    // Gestion des localités
    if (address.suburb) components.push(address.suburb);
    if (address.city || address.town || address.village) {
      components.push(address.city || address.town || address.village);
    }

    // Gestion des codes postaux
    if (address.postcode) components.push(address.postcode);

    // Gestion du pays
    if (address.country) components.push(address.country);

    return components.length > 0 
      ? components.filter(Boolean).join(', ')
      : address.display_name || 'Adresse non disponible';
  };

  const toggleSearchMode = () => {
    setSearchMode(prev => prev === 'address' ? 'project' : 'address');
    setSearchTerm('');
    setSuggestions([]);
    setError(null);
    setIsSearching(false);
    setActiveIndex(0);
  };

  return (
    <div className="search-container" role="search">
      <label htmlFor="search-input" className="visually-hidden">
        {searchMode === 'address' ? 'Rechercher une adresse' : 'Rechercher un projet'}
      </label>
      <input
        type="text"
        className="search-input"
        id="search-input"
        name="search-input"
        value={searchTerm}
        onChange={handleInputChange}
        placeholder={searchMode === 'address' ? "Rechercher une adresse..." : "Rechercher un projet..."}
        aria-label={searchMode === 'address' ? "Rechercher une adresse" : "Rechercher un projet"}
        aria-autocomplete="list"
        aria-controls="suggestions-list"
        aria-activedescendant={suggestions.length > 0 ? `suggestion-${activeIndex}` : undefined}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && activeIndex < suggestions.length - 1) {
            setActiveIndex(prev => prev + 1);
          } else if (e.key === 'ArrowUp' && activeIndex > 0) {
            setActiveIndex(prev => prev - 1);
          } else if (e.key === 'Enter' && suggestions[activeIndex]) {
            handleSelect(suggestions[activeIndex]);
          }
        }}
      />
      {searchTerm && (
        <button 
          className="clear-button" 
          onClick={handleClear}
          aria-label="Effacer la recherche"
        >
          ✕
        </button>
      )}
      <button
        className="search-mode-toggle"
        onClick={toggleSearchMode}
        aria-label={`Basculer vers la recherche ${searchMode === 'address' ? 'de projets' : 'd\'adresses'}`}
      >
        {searchMode === 'address' ? <FaBuilding /> : <FaMapMarkerAlt />}
      </button>
      <FaSearch className="search-icon" aria-hidden="true" />
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}
      {isSearching && suggestions.length > 0 && (
        <ul 
          className="suggestions-list" 
          id="suggestions-list"
          role="listbox"
          aria-label={searchMode === 'address' ? "Suggestions d'adresses" : "Suggestions de projets"}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={searchMode === 'address' ? suggestion.place_id : suggestion.id}
              className={`suggestion-item ${index === activeIndex ? 'active' : ''}`}
              onClick={() => handleSelect(suggestion)}
              onMouseEnter={() => setActiveIndex(index)}
              role="option"
              id={`suggestion-${index}`}
              aria-selected={index === activeIndex}
            >
              {searchMode === 'address' ? formatAddress(suggestion.address) : (
                <div className="project-suggestion">
                  <span className="project-title">{suggestion.titre}</span>
                  {suggestion.architectes && suggestion.architectes.length > 0 && (
                    <span className="architect-name">
                      - {suggestion.architectes[0].nom}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
